import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  APP_TEMPLATES,
  AppTemplate,
  ColorMode,
  RADIUS_OPTIONS,
  RadiusPreset,
  THEME_PRESETS,
  ThemeConfig,
  ThemeContextValue,
  ThemePreset,
} from "./types";

const STORAGE_KEY = "etsy-fw-theme";

const DEFAULT_THEME: ThemeConfig = {
  colorMode: "system",
  preset: "mint",
  radius: "lg",
  template: "clean",
};

const ThemeContext = createContext<ThemeContextValue | null>(null);

function resolveColorMode(mode: ColorMode): "light" | "dark" {
  if (mode === "system") {
    return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
  }
  return mode;
}

function applyTheme(theme: ThemeConfig, resolved: "light" | "dark") {
  const root = document.documentElement;

  // Color mode
  root.classList.toggle("dark", resolved === "dark");

  // Template — sets data-template (baseline layout, typography, radius, sidebar)
  root.removeAttribute("data-template");
  root.setAttribute("data-template", theme.template);

  // Color preset (Accent Color) — data-theme takes priority over template default primary
  root.removeAttribute("data-theme");
  if (theme.preset) {
    root.setAttribute("data-theme", theme.preset);
  }

  // Radius — data-radius takes priority over template default radius
  root.removeAttribute("data-radius");
  if (theme.radius) {
    root.setAttribute("data-radius", theme.radius);
  }

  // Explicit custom font overrides (remove inline property when not set so data-template CSS variables take effect)
  if (theme.fontSans) {
    root.style.setProperty("--font-sans", theme.fontSans);
  } else {
    root.style.removeProperty("--font-sans");
  }
  if (theme.fontMono) {
    root.style.setProperty("--font-mono", theme.fontMono);
  } else {
    root.style.removeProperty("--font-mono");
  }
}

interface ThemeProviderProps {
  children: React.ReactNode;
  defaultTheme?: Partial<ThemeConfig>;
  storageKey?: string;
}

export function ThemeProvider({
  children,
  defaultTheme,
  storageKey = STORAGE_KEY,
}: ThemeProviderProps) {
  const [theme, setThemeState] = useState<ThemeConfig>(() => {
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        const parsed = JSON.parse(stored) as Partial<ThemeConfig>;
        return { ...DEFAULT_THEME, ...defaultTheme, ...parsed };
      }
    } catch {}
    return { ...DEFAULT_THEME, ...defaultTheme };
  });

  const [resolvedColorMode, setResolvedColorMode] = useState<"light" | "dark">(
    () => resolveColorMode(theme.colorMode)
  );

  useEffect(() => {
    const resolved = resolveColorMode(theme.colorMode);
    setResolvedColorMode(resolved);
    applyTheme(theme, resolved);
    try { localStorage.setItem(storageKey, JSON.stringify(theme)); } catch {}
  }, [theme, storageKey]);

  useEffect(() => {
    if (theme.colorMode !== "system") return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = () => {
      const resolved = resolveColorMode("system");
      setResolvedColorMode(resolved);
      applyTheme(theme, resolved);
    };
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, [theme]);

  const setColorMode  = useCallback((colorMode: ColorMode) => setThemeState((p) => ({ ...p, colorMode })), []);
  const setPreset     = useCallback((preset: ThemePreset)   => setThemeState((p) => ({ ...p, preset })), []);
  const setRadius     = useCallback((radius: RadiusPreset)  => setThemeState((p) => ({ ...p, radius })), []);
  const setTemplate   = useCallback((template: AppTemplate) => {
    setThemeState((p) => {
      const next = { ...p, template };
      delete next.fontSans;
      return next;
    });
  }, []);

  const value = useMemo<ThemeContextValue>(
    () => ({
      theme,
      setColorMode,
      setPreset,
      setRadius,
      setTemplate,
      resolvedColorMode,
      presets: THEME_PRESETS,
      radiusOptions: RADIUS_OPTIONS,
      templates: APP_TEMPLATES,
    }),
    [theme, setColorMode, setPreset, setRadius, setTemplate, resolvedColorMode]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used inside <ThemeProvider>");
  return ctx;
}
